import React, { useEffect, useState, useRef } from "react";
import Layout from "../components/Layout";
import api, { inr, formatApiError, isAdmin, hasWrite } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { RefreshCw, Plug, CheckCircle2, Plus, Link2, AlertTriangle, Search } from "lucide-react";
import { StorePartialDialog, StoreModeBadge } from "../components/StorePartialDialog";
import { UnlinkedBillFinder } from "../components/UnlinkedBillFinder";

const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring";
const label = "text-sm font-medium block mb-1";
const modeColor = (m) => ({ cash: "bg-[hsl(var(--positive))]/10 text-[hsl(var(--positive))]", qr: "bg-accent/10 text-accent", upi: "bg-accent/10 text-accent", bank: "bg-primary/10 text-primary", partial: "bg-destructive/10 text-destructive", credit: "bg-destructive/15 text-destructive" }[(m || "").toLowerCase()] || "bg-muted text-muted-foreground");
const syncedLabel = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

export default function StoreInvoices() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const canWrite = hasWrite(user);
  const [cfg, setCfg] = useState(null);
  const [showCfg, setShowCfg] = useState(false);
  const [cfgForm, setCfgForm] = useState({ api_url: "", email: "", password: "", garage_id: "" });
  const [garages, setGarages] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState("");
  const [err, setErr] = useState("");
  const [testing, setTesting] = useState(false);
  const [fromDate, setFromDate] = useState("2026-10-20");
  const [toDate, setToDate] = useState("");
  const [showFinder, setShowFinder] = useState(false);
  const rangeRef = useRef({ from: "2026-10-20", to: "" });
  rangeRef.current = { from: fromDate, to: toDate };

  const loadCfg = async () => {
    try {
      const { data } = await api.get("/store-sync/config");
      setCfg(data);
      setCfgForm((f) => ({ ...f, api_url: data.api_url || "", email: data.email || "", garage_id: data.garage_id || "" }));
      if (data.garage_id) setGarages((g) => (g.length ? g : [{ id: data.garage_id, name: data.garage_name || data.garage_id }]));
      if (!data.configured && admin) setShowCfg(true);
    } catch { /* noop */ }
  };
  const loadBills = async (range = rangeRef.current) => {
    setLoading(true); setErr("");
    try { setData((await api.get("/store-sync/bills", { params: { from_date: range.from || undefined, to_date: range.to || undefined } })).data); }
    catch (e) {
      const msg = formatApiError(e?.response?.data?.detail ?? e?.response?.data ?? e?.message);
      setErr(msg); setData(null);
    }
    setLoading(false);
  };
  useEffect(() => { loadCfg(); }, []);
  useEffect(() => { if (cfg?.configured) loadBills(); }, [cfg?.configured]); // eslint-disable-line
  // Auto-refresh the open page every minute so bills stay fresh without a manual Refresh.
  useEffect(() => {
    if (!cfg?.configured) return;
    const id = setInterval(loadBills, 60 * 1000);
    return () => clearInterval(id);
  }, [cfg?.configured]); // eslint-disable-line

  const saveCfg = async () => {
    if (!cfgForm.api_url.trim() || !cfgForm.email.trim()) return toast.error("Enter the API URL and email");
    if (!cfg?.configured && !cfgForm.password.trim()) return toast.error("Enter the password");
    if (garages.length > 1 && !cfgForm.garage_id) return toast.error("Pick the AutoGarage garage for this company");
    const g = garages.find((x) => x.id === cfgForm.garage_id);
    try {
      await api.put("/store-sync/config", { api_url: cfgForm.api_url.trim(), email: cfgForm.email.trim(), password: cfgForm.password || undefined, garage_id: cfgForm.garage_id || undefined, garage_name: g?.name });
      toast.success("Connection saved");
      setShowCfg(false);
      setCfgForm((f) => ({ ...f, password: "" }));
      await loadCfg();
      await loadBills();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const testCfg = async () => {
    if (!cfgForm.api_url.trim() || !cfgForm.email.trim()) return toast.error("Enter the API URL and email");
    if (!cfg?.configured && !cfgForm.password.trim()) return toast.error("Enter the password");
    setTesting(true);
    try {
      const { data } = await api.post("/store-sync/test", { api_url: cfgForm.api_url.trim(), email: cfgForm.email.trim(), password: cfgForm.password || undefined });
      toast.success(data.message || "Connection successful");
      setGarages(data.garages || []);
      if ((data.garages || []).length === 1) setCfgForm((f) => ({ ...f, garage_id: data.garages[0].id }));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail ?? e?.response?.data ?? e?.message)); }
    setTesting(false);
  };

  const [partialBill, setPartialBill] = useState(null);
  const addOutside = async (b, split) => {
    if (b.payment_mode === "partial" && !split) return setPartialBill(b);
    if (!split && !window.confirm(`Add Outside invoice for bill ${b.bill_no} (${inr(b.amount)}) and auto-generate a receipt on ${b.date}?`)) return;
    setAdding(b.bill_no);
    try {
      const { data: res } = await api.post("/store-sync/add-outside", { bill_no: b.bill_no, date: b.date, amount: b.amount, payment_mode: b.payment_mode, vehicle_number: b.vehicle_number, mobile: b.mobile, ...(split || {}) });
      toast.success(res.credit ? `Credit invoice added (payment pending)` : res.partial ? `Partial invoice added · cash + ${split.online_method.toUpperCase()} posted to cash book` : `Outside invoice added · receipt ${res.receipt_no}`);
      setPartialBill(null);
      await loadBills();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setAdding("");
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Store Invoices</h1>
            <p className="text-sm text-muted-foreground mt-1">Bills from your AutoGarage POS. Link them to DEF/Tata invoices by challan, or add as Outside invoices.</p>
            {cfg?.garage_name && <p data-testid="store-linked-garage" className="text-xs text-muted-foreground mt-1">Garage: <span className="font-medium text-foreground">{cfg.garage_name}</span></p>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {cfg?.configured && (
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-muted-foreground hidden sm:inline">From</span>
                <input data-testid="store-from-date" type="date" min="2026-10-20" value={fromDate}
                  onChange={(e) => { const v = e.target.value; setFromDate(v); loadBills({ from: v, to: toDate }); }}
                  className="h-10 px-2 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40" />
                <span className="text-xs text-muted-foreground hidden sm:inline">To</span>
                <input data-testid="store-to-date" type="date" min="2026-10-20" value={toDate}
                  onChange={(e) => { const v = e.target.value; setToDate(v); loadBills({ from: fromDate, to: v }); }}
                  className="h-10 px-2 rounded-md border border-input bg-card text-sm num focus:outline-none focus:ring-2 focus:ring-ring/40" />
              </div>
            )}
            {cfg?.configured && data?.last_synced && !err && (
              <span data-testid="store-last-synced" className="text-xs text-muted-foreground hidden sm:inline num">Synced {syncedLabel(data.last_synced)}</span>
            )}
            {admin && (
              <button data-testid="store-config-btn" onClick={() => setShowCfg((v) => !v)} className="h-10 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors"><Plug size={15} /> Connection</button>
            )}
            {cfg?.configured && (
              <button data-testid="store-refresh-btn" onClick={loadBills} disabled={loading} className="h-10 px-4 rounded-md bg-primary text-primary-foreground text-sm flex items-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-50"><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> {loading ? "Loading…" : "Refresh"}</button>
            )}
          </div>
        </div>

        {showCfg && admin && (
          <div data-testid="store-config-panel" className="rounded-xl border border-border bg-card p-4 mb-6 space-y-3 animate-fade-up">
            <div className="text-sm font-medium">AutoGarage connection</div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><label className={label}>API URL</label><input data-testid="store-cfg-url" className={field} value={cfgForm.api_url} onChange={(e) => setCfgForm({ ...cfgForm, api_url: e.target.value })} placeholder="https://auto-inventory-hub-37.emergent.host/api" /></div>
              <div><label className={label}>Login email</label><input data-testid="store-cfg-email" className={field} value={cfgForm.email} onChange={(e) => setCfgForm({ ...cfgForm, email: e.target.value })} placeholder="khata-sync@yourco.com" /></div>
              <div><label className={label}>Password {cfg?.configured && <span className="text-muted-foreground font-normal">(leave blank to keep)</span>}</label><input data-testid="store-cfg-password" type="password" className={field} value={cfgForm.password} onChange={(e) => setCfgForm({ ...cfgForm, password: e.target.value })} placeholder="••••••••" /></div>
            </div>
            {garages.length > 0 && (
              <div className="sm:max-w-sm"><label className={label}>AutoGarage garage for this company</label>
                <select data-testid="store-cfg-garage" className={field} value={cfgForm.garage_id} onChange={(e) => setCfgForm({ ...cfgForm, garage_id: e.target.value })}>
                  <option value="">Select garage…</option>
                  {garages.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select></div>
            )}
            <div className="flex justify-end gap-2">
              <button data-testid="store-cfg-test" onClick={testCfg} disabled={testing}
                className="h-10 px-5 rounded-md border border-input bg-card text-sm font-medium flex items-center gap-2 hover:bg-muted transition-colors disabled:opacity-50">
                <Plug size={15} className={testing ? "animate-pulse" : ""} /> {testing ? "Testing…" : "Test connection"}
              </button>
              <button data-testid="store-cfg-save" onClick={saveCfg} className="h-10 px-5 rounded-md bg-accent text-accent-foreground text-sm font-medium hover:bg-accent/90 transition-colors">Save & connect</button>
            </div>
          </div>
        )}

        {!cfg?.configured && !showCfg && (
          <div className="rounded-xl border border-dashed border-border bg-card p-10 text-center text-muted-foreground">
            <Plug size={28} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm">{admin ? "Connect your AutoGarage POS to start pulling store bills." : "Ask your admin to connect the AutoGarage POS."}</p>
          </div>
        )}

        {err && cfg?.configured && (
          <div data-testid="store-error-banner" className="rounded-xl border border-destructive/25 bg-destructive/5 p-4 mb-6 flex items-start gap-3 animate-fade-up">
            <AlertTriangle size={20} className="text-destructive mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-destructive">Couldn't load bills from AutoGarage</div>
              <div className="text-sm text-muted-foreground mt-0.5">{err}</div>
              <div className="flex flex-wrap gap-2 mt-3">
                <button data-testid="store-error-retry" onClick={loadBills} disabled={loading}
                  className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm flex items-center gap-2 hover:bg-primary/90 transition-colors disabled:opacity-50">
                  <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {loading ? "Retrying…" : "Retry"}
                </button>
                {admin && (
                  <button data-testid="store-error-connection" onClick={() => setShowCfg(true)}
                    className="h-9 px-4 rounded-md border border-input bg-card text-sm flex items-center gap-2 hover:bg-muted transition-colors">
                    <Plug size={14} /> Check connection
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {data?.stale && (
          <div data-testid="store-stale-banner" className="rounded-xl border border-accent/30 bg-accent/5 p-3.5 mb-4 flex items-start gap-2.5 animate-fade-up">
            <AlertTriangle size={18} className="text-accent mt-0.5 shrink-0" />
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Showing last saved copy</span> (synced {syncedLabel(data.last_synced)}). Couldn't reach AutoGarage just now{data.error ? ` — ${data.error}` : ""}.
            </div>
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-5">
              <div className="rounded-lg border border-border bg-card px-4 py-3"><div className="text-xs text-muted-foreground">Total bills</div><div className="num text-xl font-bold">{data.count}</div></div>
              <div className="rounded-lg border border-[hsl(var(--positive))]/20 bg-[hsl(var(--positive))]/5 px-4 py-3"><div className="text-xs text-muted-foreground">Linked</div><div className="num text-xl font-bold text-[hsl(var(--positive))]">{data.linked}</div></div>
              <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div><div className="text-xs text-muted-foreground">Unlinked</div><div className="num text-xl font-bold text-destructive">{data.unlinked}</div></div>
                {data.unlinked > 0 && (
                  <button data-testid="open-unlinked-finder" onClick={() => setShowFinder(true)} className="h-8 px-3 rounded-md border border-destructive/30 bg-card text-xs font-medium text-destructive flex items-center gap-1 hover:bg-destructive/10 transition-colors"><Search size={13} /> Find matches</button>
                )}
              </div>
            </div>
            {showFinder && <UnlinkedBillFinder canWrite={canWrite} onClose={() => setShowFinder(false)} onLinked={loadBills} />}
            <div className="rounded-xl border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-muted text-left text-xs uppercase text-muted-foreground">
                  <th className="px-4 py-3">Date</th><th className="px-4 py-3">Bill / Invoice No</th><th className="px-4 py-3">Vehicle</th><th className="px-4 py-3">Qty / items</th><th className="px-4 py-3">Mode</th><th className="px-4 py-3">Mobile</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
                </tr></thead>
                <tbody>
                  {data.bills.map((b) => (
                    <tr key={b.bill_no} data-testid={`store-bill-${b.bill_no}`} className="border-t border-border hover:bg-muted/40 transition-colors">
                      <td className="px-4 py-3 num whitespace-nowrap">{b.date}</td>
                      <td className="px-4 py-3 num font-semibold">{b.bill_no}</td>
                      <td className="px-4 py-3 num">{b.vehicle_number || "—"}</td>
                      <td data-testid={`store-qty-${b.bill_no}`} className="px-4 py-3 text-xs max-w-[220px]" title={b.items || ""}>
                        {b.qty != null ? <><span className="num font-semibold">{b.qty}</span> <span className="text-muted-foreground line-clamp-2">{b.items}</span></> : "—"}
                      </td>
                      <td className="px-4 py-3"><StoreModeBadge b={b} cls={modeColor(b.payment_mode)} /></td>
                      <td className="px-4 py-3 num">{b.mobile || "—"}</td>
                      <td className="px-4 py-3 num text-right font-semibold">{inr(b.amount)}</td>
                      <td className="px-4 py-3">
                        {b.linked ? (
                          <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--positive))] font-medium"><Link2 size={13} /> {b.linked_source === "outside" ? "Outside" : "Tata/DEF"} · {b.linked_invoice_no}</span>
                        ) : (
                          <span className="text-xs text-destructive font-medium">Unlinked</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {!b.linked && canWrite && (
                          <button data-testid={`store-add-outside-${b.bill_no}`} onClick={() => addOutside(b)} disabled={adding === b.bill_no}
                            className="h-8 px-3 rounded-md bg-accent text-accent-foreground text-xs flex items-center gap-1 ml-auto hover:bg-accent/90 transition-colors disabled:opacity-50">
                            <Plus size={13} /> {adding === b.bill_no ? "Adding…" : "Add Outside Invoice"}
                          </button>
                        )}
                        {b.linked && <CheckCircle2 size={16} className="text-[hsl(var(--positive))] ml-auto" />}
                      </td>
                    </tr>
                  ))}
                  {data.bills.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">No bills found.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      {partialBill && <StorePartialDialog bill={partialBill} onClose={() => setPartialBill(null)} onSubmit={(s) => addOutside(partialBill, s)} />}
    </Layout>
  );
}
